import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Award,
  Crosshair,
  Droplet,
  Flame,
  Medal,
  Radar,
  Shield,
  Target,
  Trophy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  useLeaderboard,
  useMyStats,
  type LeaderboardBadge,
  type LeaderboardEntry,
} from '../hooks/useLeaderboard';
import { Badge, PageContainer, PageHeader, Section } from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';

/**
 * Leaderboard — clean professional gamification view. A PageHeader (where you
 * are + your standing), a top-3 highlight strip, a ranked table of analysts, and
 * a badges showcase comparing the caller's earned badges against the catalog.
 * Data comes from `useLeaderboard` + `useMyStats`, both backed by
 * services/score/handler.py. Loading / empty / error states preserved.
 */

export const Route = createFileRoute('/leaderboard')({
  component: LeaderboardPage,
});

// lucide icon name (from the backend badge catalog) → component.
const BADGE_ICONS: Record<string, LucideIcon> = {
  droplet: Droplet,
  crosshair: Crosshair,
  flame: Flame,
  radar: Radar,
  shield: Shield,
};

function badgeIcon(name: string): LucideIcon {
  return BADGE_ICONS[name] ?? Award;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// Per-rank accent for the top-3 highlight (1 gold, 2 silver, 3 bronze).
const PODIUM: Record<number, { ring: string; text: string; bg: string; label: string }> = {
  1: { ring: 'ring-hud-amber/50', text: 'text-hud-amber', bg: 'bg-hud-amber/10', label: 'Gold' },
  2: { ring: 'ring-txt-muted/40', text: 'text-txt-2', bg: 'bg-panel', label: 'Silver' },
  3: { ring: 'ring-hud-cyan/40', text: 'text-hud-cyan', bg: 'bg-hud-cyan/10', label: 'Bronze' },
};

function LeaderboardPage() {
  const { data, isPending, isError, error } = useLeaderboard();
  const { data: me } = useMyStats();

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Trophy}
        title="Leaderboard"
        description="Difficulty-weighted rankings across your tenant — points, scenarios completed, and detection accuracy."
        breadcrumbs={[{ label: 'Training' }, { label: 'Leaderboard' }]}
        actions={
          data ? (
            <div className="flex items-center gap-2">
              <Badge variant="neutral">{data.count} ranked</Badge>
              {me ? <Badge variant="info">You · {me.points.toLocaleString()} pts</Badge> : null}
            </div>
          ) : undefined
        }
      />

      {isError ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-hud-red/40 bg-hud-red/10 p-4">
          <Target className="mt-0.5 size-4 shrink-0 text-hud-red" />
          <div>
            <div className="text-[13px] font-semibold text-hud-red">Could not load leaderboard</div>
            <div className="mt-0.5 font-mono text-[11px] text-txt-2">
              {(error as Error)?.message ?? 'Unknown error'}
            </div>
          </div>
        </div>
      ) : isPending || !data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-[120px] animate-pulse rounded-xl border border-bd bg-card" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="h-[420px] animate-pulse rounded-xl border border-bd bg-card lg:col-span-8" />
            <div className="h-[420px] animate-pulse rounded-xl border border-bd bg-card lg:col-span-4" />
          </div>
        </div>
      ) : (
        <>
          {/* Top 3 highlight */}
          <Section title="Top performers" description="The three highest-scoring analysts this season.">
            <Podium entries={data.entries.slice(0, 3)} />
          </Section>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            {/* Ranked table */}
            <div className="lg:col-span-8">
              <Section
                title="Rankings"
                description="Every ranked analyst, ordered by difficulty-weighted points."
                action={<span className="text-[12px] text-txt-muted">{data.entries.length} players</span>}
              >
                {data.entries.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-bd bg-card py-10 text-center text-[12.5px] text-txt-2">
                    No scores recorded yet — complete a scenario to get on the board.
                  </div>
                ) : (
                  <div className="hud-panel overflow-hidden p-0">
                    {/* header row */}
                    <div className="grid grid-cols-[2.75rem_1fr_5.5rem_4rem_4.5rem] items-center gap-2 border-b border-bd px-4 py-2.5 text-[11px] font-medium text-txt-muted">
                      <span className="text-center">#</span>
                      <span>Analyst</span>
                      <span className="text-right">Points</span>
                      <span className="text-right">Scenarios</span>
                      <span className="text-right">Accuracy</span>
                    </div>
                    <div className="divide-y divide-bd">
                      {data.entries.map((e) => (
                        <Row key={e.user_id} entry={e} highlight={me?.user_id === e.user_id} />
                      ))}
                    </div>
                  </div>
                )}
              </Section>
            </div>

            {/* Badges showcase */}
            <div className="lg:col-span-4">
              <BadgeShowcase catalog={data.badge_catalog} earned={me?.badges ?? []} />
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
}

function Podium({ entries }: { entries: ReadonlyArray<LeaderboardEntry> }) {
  // Visual order: 2nd, 1st, 3rd — 1st centered + emphasised.
  const order = [entries[1], entries[0], entries[2]].filter(Boolean) as LeaderboardEntry[];
  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="show"
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      {order.map((e) => {
        const accent = PODIUM[e.rank] ?? PODIUM[3]!;
        const isFirst = e.rank === 1;
        return (
          <motion.div
            key={e.user_id}
            variants={cardItem}
            whileHover={{ y: -2 }}
            className={`hud-panel p-4 ${isFirst ? 'sm:order-2 sm:-translate-y-1.5' : e.rank === 2 ? 'sm:order-1' : 'sm:order-3'}`}
          >
            <div className="flex items-center justify-between">
              <span className={`flex items-center gap-1.5 text-[12px] font-semibold ${accent.text}`}>
                <Medal className="size-3.5" />
                {accent.label}
              </span>
              <span className="rounded-full border border-bd bg-panel px-2 py-0.5 font-mono text-[10px] text-txt-muted">
                #{e.rank}
              </span>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div
                className={`flex shrink-0 items-center justify-center rounded-xl font-display font-bold ring-1 ${accent.ring} ${accent.bg} ${accent.text} ${
                  isFirst ? 'size-14 text-lg' : 'size-12 text-base'
                }`}
              >
                {initials(e.name)}
              </div>
              <div className="min-w-0">
                <div className="truncate text-[14px] font-semibold text-txt-bright">{e.name}</div>
                <div className={`readout__value mt-0.5 text-2xl ${accent.text}`}>
                  {e.points.toLocaleString()}
                  <span className="ml-1 text-[11px] font-normal text-txt-muted">pts</span>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

function Row({ entry, highlight }: { entry: LeaderboardEntry; highlight: boolean }) {
  const accent = PODIUM[entry.rank];
  return (
    <div
      className={`grid grid-cols-[2.75rem_1fr_5.5rem_4rem_4.5rem] items-center gap-2 px-4 py-2.5 transition-colors duration-150 hover:bg-card-hover ${
        highlight ? 'bg-neon-glow' : ''
      }`}
    >
      <span
        className={`tabular text-center font-mono text-[12px] font-bold ${accent ? accent.text : 'text-txt-muted'}`}
      >
        {String(entry.rank).padStart(2, '0')}
      </span>
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className={`flex size-7 shrink-0 items-center justify-center rounded-lg bg-panel text-[10px] font-semibold ${
            accent ? accent.text : 'text-txt-2'
          } ${accent ? `ring-1 ${accent.ring}` : ''}`}
        >
          {initials(entry.name)}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 truncate text-[13px] font-semibold text-txt-bright">
            {entry.name}
            {highlight ? <span className="text-[11px] font-medium text-neon">you</span> : null}
          </div>
          {entry.badges.length > 0 ? (
            <div className="mt-0.5 flex items-center gap-1">
              {entry.badges.slice(0, 4).map((b) => {
                const Icon = badgeIcon(b.icon);
                return <Icon key={b.id} className="size-3 text-hud-cyan" aria-label={b.label} />;
              })}
            </div>
          ) : null}
        </div>
      </div>
      <span className="tabular text-right font-mono text-[13px] font-semibold text-neon">
        {entry.points.toLocaleString()}
      </span>
      <span className="tabular text-right font-mono text-[12px] text-txt-2">{entry.scenarios_completed}</span>
      <span className="tabular text-right font-mono text-[12px] text-txt-2">
        {Math.round(entry.accuracy * 100)}%
      </span>
    </div>
  );
}

function BadgeShowcase({
  catalog,
  earned,
}: {
  catalog: ReadonlyArray<LeaderboardBadge>;
  earned: ReadonlyArray<LeaderboardBadge>;
}) {
  const earnedIds = new Set(earned.map((b) => b.id));
  return (
    <Section
      title="Badges"
      description="Your achievements against the full catalog."
      action={
        <span className="text-[12px] text-txt-muted">
          {earnedIds.size}/{catalog.length}
        </span>
      }
    >
      <div className="space-y-2">
        {catalog.map((b) => {
          const Icon = badgeIcon(b.icon);
          const has = earnedIds.has(b.id);
          return (
            <div
              key={b.id}
              className={`flex items-center gap-2.5 rounded-xl border p-3 transition-colors duration-150 ${
                has ? 'border-bd-bright bg-neon-glow' : 'border-bd bg-card opacity-60'
              }`}
            >
              <span
                className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${
                  has ? 'bg-neon/15 text-neon' : 'bg-panel text-txt-muted'
                }`}
              >
                <Icon className="size-4" />
              </span>
              <div className="min-w-0">
                <div className="truncate text-[12.5px] font-semibold text-txt-bright">{b.label}</div>
                <div className="truncate text-[11px] text-txt-muted">{b.description}</div>
              </div>
              {has ? (
                <Badge variant="success" size="xs">
                  Earned
                </Badge>
              ) : (
                <Badge variant="neutral" size="xs">
                  Locked
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}
